/**
 * 수수료 정보 종합 — 레거시 `commissionRateAggregator.js` 포팅.
 * 비교파일 → 추출 대상 → 종합 추출·다운로드 흐름과 안내 문구를 레거시에 맞춘다.
 */
import { getCurrentRefYm } from '@/pages/fee-rates/feeRateUtils';
import { COMMISSION_EXPORT_HEADER_MAP } from '@/pages/settlement-fees/commissionSheetParser';
import { useCommissionRateAggregator } from '@/pages/settlement-fees/useCommissionRateAggregator';
import { MonthPicker } from '@/shared/components/DatePicker';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { StepLockedSection } from '@/shared/components/StepLockedSection';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/shared/components/ui/alert';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Label } from '@/shared/components/ui/label';
import { FilterRow, ToolbarRow } from '@/shared/components/ui/row';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { downloadXlsx } from '@/shared/utils/xlsxExport';
import { useToast } from '@/shared/utils/toast';
import {
  Download,
  FileSpreadsheet,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import React, { useEffect, useRef } from 'react';

const STATUS_LABEL: Record<string, string> = {
  parsed: '반영됨',
  unmatched: '형식 제외',
  unsupported: '형식 제외',
  error: '오류',
};

const PREVIEW_STATUS_LABEL: Record<string, string> = {
  idle: '-',
  loading: '추출 확인 중…',
  matched: '매칭',
  unmatched: '미매칭',
  error: '오류',
};

const formatNumber = (value: number) => value.toLocaleString('ko-KR');

export const CommissionRateAggregatorPage: React.FC = () => {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const compareInputRef = useRef<HTMLInputElement>(null);
  const {
    selectedFiles,
    fileMeta,
    previousMonthFile,
    setComparisonFile,
    referenceYearMonth,
    setReferenceYearMonth,
    result,
    isLoading,
    errorMessage,
    isComparisonFileReady,
    isTargetFilesReady,
    isAggregateStepReady,
    addFiles,
    removeFile,
    clearAll,
    aggregate,
    exportDriveWorkbook,
    refreshFilePreviews,
  } = useCommissionRateAggregator();

  useEffect(() => {
    if (!referenceYearMonth) {
      setReferenceYearMonth(getCurrentRefYm());
    }
  }, [referenceYearMonth, setReferenceYearMonth]);

  const aggregateStepLockMessage = !isComparisonFileReady
    ? {
        title: '1단계를 먼저 완료해 주세요',
        description:
          '비교파일을 업로드하면 종합 추출 · 편집을 사용할 수 있습니다.',
      }
    : {
        title: '2단계를 먼저 완료해 주세요',
        description:
          '추출 대상 파일을 하나 이상 추가하면 종합 추출 · 편집을 사용할 수 있습니다.',
      };

  const handleAggregate = async () => {
    if (!isComparisonFileReady) {
      toast.error(
        '종합 추출 전에 비교파일(이전 월 수수료 종합 xlsx)을 첨부해 주세요. 비교파일은 참고용 비교 기준으로만 사용됩니다.',
      );
      return;
    }
    if (!referenceYearMonth || referenceYearMonth.length !== 6) {
      toast.error('기준년월(YYYYMM)을 선택해 주세요.');
      return;
    }
    const parsed = await aggregate();
    if (parsed?.rows.length) {
      toast.success(`${formatNumber(parsed.rows.length)}건을 추출했습니다.`);
    }
  };

  const handleExport = () => {
    if (result?.driveRows?.length) {
      exportDriveWorkbook();
      return;
    }
    if (!result?.rows.length) return;
    downloadXlsx(
      result.rows as unknown as Record<string, unknown>[],
      `수수료정보종합_${referenceYearMonth}`,
      COMMISSION_EXPORT_HEADER_MAP,
    );
  };

  const canClear =
    selectedFiles.length > 0 || Boolean(result) || Boolean(previousMonthFile);

  const isPreviewLoading = selectedFiles.some(
    (file) => fileMeta[file.id]?.previewStatus === 'loading',
  );

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FileSpreadsheet}
        title="수수료 정보 종합"
        description="제약사별 수수료율 엑셀을 추출·종합합니다. 먼저 비교파일(이전 월 종합 xlsx)을 올린 뒤, 추출 대상 파일을 추가하고 종합 추출·편집을 실행하세요. 비교파일은 참고용으로만 사용하며, 이번 추출 결과와 제품코드 우선·제품명 차선 매칭으로 비교합니다."
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">1. 비교파일 업로드</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            종합 추출 시 참고용 비교 기준으로 사용할 수수료 종합 xlsx를
            첨부합니다. 비교파일은 수정·반영되지 않습니다.
          </p>
          <ToolbarRow gap={12}>
            <MenuWriteGate>
              <FileUploadButton
                inputRef={compareInputRef}
                className="ruda-button-outline"
                accept=".xlsx,.xls"
                onFilesSelected={(files) =>
                  setComparisonFile(files?.[0] ?? null)
                }
              >
                {previousMonthFile ? '비교파일 변경' : '비교파일 선택'}
              </FileUploadButton>
            </MenuWriteGate>
            {isComparisonFileReady ? (
              <Badge variant="outline" className="text-green-700">
                완료
              </Badge>
            ) : null}
          </ToolbarRow>

          <Alert variant={previousMonthFile ? 'default' : 'warning'}>
            <AlertTitle>
              {previousMonthFile
                ? `비교파일: ${previousMonthFile.name}`
                : '비교파일이 첨부되지 않았습니다.'}
            </AlertTitle>
            <AlertDescription>
              {previousMonthFile ? (
                <ToolbarRow gap={8} className="mt-1">
                  <span className="text-muted-foreground">
                    종합 추출 시 이 파일은 비교 기준으로만 사용됩니다.
                  </span>
                  <MenuWriteGate>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto px-0"
                      onClick={() => setComparisonFile(null)}
                    >
                      첨부 해제
                    </Button>
                  </MenuWriteGate>
                </ToolbarRow>
              ) : (
                '「비교파일 선택」으로 이전 월 종합 xlsx를 올려 주세요.'
              )}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <StepLockedSection
        locked={!isComparisonFileReady}
        lockTitle="1단계를 먼저 완료해 주세요"
        lockDescription="비교파일을 업로드하면 추출 대상 파일 업로드를 사용할 수 있습니다."
        minHeight={200}
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3">
            <CardTitle className="text-base">2. 추출대상 파일 업로드</CardTitle>
            <div>
              <MenuWriteGate>
                <FileUploadButton
                  inputRef={inputRef}
                  className="ruda-button-outline"
                  accept=".xlsx,.xls"
                  multiple
                  disabled={!isComparisonFileReady}
                  onFilesSelected={(files) => addFiles(files)}
                >
                  파일 선택
                </FileUploadButton>
              </MenuWriteGate>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertTitle>
                선택 파일 {formatNumber(selectedFiles.length)}개
              </AlertTitle>
              <AlertDescription>
                파일을 추가하면 아래 목록에서 템플릿 매칭과 추출 설정을 확인할 수
                있습니다.
              </AlertDescription>
            </Alert>

            {selectedFiles.length > 0 ? (
              <div className="space-y-3">
                <ToolbarRow justify="between" gap={8} className="flex-wrap">
                  <p className="text-sm text-muted-foreground">
                    총 {formatNumber(selectedFiles.length)}개
                    {isPreviewLoading ? ' · 추출 확인 중…' : ''}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void refreshFilePreviews()}
                    disabled={!isComparisonFileReady || isPreviewLoading}
                  >
                    <RefreshCw />
                    전체 재확인
                  </Button>
                </ToolbarRow>

                <div className="rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>파일명</TableHead>
                        <TableHead>템플릿명</TableHead>
                        <TableHead>매칭</TableHead>
                        <TableHead>크기</TableHead>
                        <TableHead>삭제</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedFiles.map((file) => {
                        const meta = fileMeta[file.id];
                        return (
                          <TableRow key={file.id}>
                            <TableCell>{file.name}</TableCell>
                            <TableCell>{meta?.templateLabel ?? '-'}</TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {PREVIEW_STATUS_LABEL[
                                  meta?.previewStatus ?? 'idle'
                                ]}
                              </Badge>
                              {meta?.previewMessage ? (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  {meta.previewMessage}
                                </span>
                              ) : null}
                            </TableCell>
                            <TableCell>
                              {formatNumber(Math.ceil(file.size / 1024))} KB
                            </TableCell>
                            <TableCell>
                              <MenuWriteGate>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeFile(file.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </MenuWriteGate>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                선택된 파일이 없습니다.
              </p>
            )}
          </CardContent>
        </Card>
      </StepLockedSection>

      <StepLockedSection
        locked={!isAggregateStepReady}
        lockTitle={aggregateStepLockMessage.title}
        lockDescription={aggregateStepLockMessage.description}
        minHeight={220}
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">3. 종합 추출 · 편집</CardTitle>
            {isTargetFilesReady && isComparisonFileReady ? (
              <Badge variant="outline" className="text-green-700">
                준비 완료
              </Badge>
            ) : null}
          </CardHeader>
          <CardContent className="space-y-4">
            <FilterRow as="div" gap={12} className="flex-wrap items-end">
              <div className="space-y-1">
                <Label>기준년월 (YYYYMM)</Label>
                <MonthPicker
                  value={referenceYearMonth}
                  onChange={(next) =>
                    setReferenceYearMonth(next || getCurrentRefYm())
                  }
                  placeholder="기준월 선택"
                  className="w-44"
                  disabled={!isAggregateStepReady}
                />
              </div>
              <MenuWriteGate>
                <Button
                  type="button"
                  className="ruda-button"
                  onClick={() => void handleAggregate()}
                  disabled={
                    isLoading ||
                    !isAggregateStepReady ||
                    !referenceYearMonth ||
                    referenceYearMonth.length !== 6
                  }
                >
                  <FileSpreadsheet />
                  {isLoading ? '종합 추출 중...' : '종합 추출 · 편집'}
                </Button>
              </MenuWriteGate>
              <MenuWriteGate>
                <Button
                  type="button"
                  variant="outline"
                  className="ruda-button-outline"
                  onClick={clearAll}
                  disabled={!canClear}
                >
                  초기화
                </Button>
              </MenuWriteGate>
              <Button
                type="button"
                variant="outline"
                onClick={handleExport}
                disabled={!result?.rows.length && !result?.driveRows?.length}
              >
                <Download />
                엑셀 다운로드
              </Button>
            </FilterRow>

            {errorMessage ? (
              <Alert variant="destructive">
                <AlertTitle>종합 처리 오류</AlertTitle>
                <AlertDescription className="whitespace-pre-wrap">
                  {errorMessage}
                </AlertDescription>
              </Alert>
            ) : null}

            {!result ? (
              <p className="text-sm text-muted-foreground">
                비교파일과 추출 대상 파일을 준비한 뒤 「종합 추출 · 편집」을
                실행하면 비교·검토 결과가 여기에 표시됩니다.
              </p>
            ) : null}
          </CardContent>
        </Card>
      </StepLockedSection>

      {result ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">전체 파일</p>
                <p className="text-2xl font-semibold">
                  {formatNumber(result.summary.totalFiles)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">추출 완료</p>
                <p className="text-2xl font-semibold">
                  {formatNumber(result.summary.parsedFiles)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">추출 행</p>
                <p className="text-2xl font-semibold">
                  {formatNumber(result.summary.extractedRows)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">제약사 수</p>
                <p className="text-2xl font-semibold">
                  {formatNumber(result.summary.pharmaceuticalCompanies)}
                </p>
              </CardContent>
            </Card>
            {result.mergeStats ? (
              <>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">코드 매칭</p>
                    <p className="text-2xl font-semibold">
                      {formatNumber(result.mergeStats.matchedCount)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">신규 추가</p>
                    <p className="text-2xl font-semibold">
                      {formatNumber(result.mergeStats.appendedCount)}
                    </p>
                  </CardContent>
                </Card>
              </>
            ) : null}
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                종합 결과 · 수수료정보종합_{referenceYearMonth || 'YYYYMM'}.xlsx
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>파일명</TableHead>
                      <TableHead>종합 처리</TableHead>
                      <TableHead>추출 행</TableHead>
                      <TableHead>메시지</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.files.map((f) => (
                      <TableRow key={f.fileName}>
                        <TableCell>{f.fileName}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {STATUS_LABEL[f.status] ?? f.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {formatNumber(f.extractedRowCount)}
                        </TableCell>
                        <TableCell>{f.message ?? '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {result.rows.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">미리보기 (상위 50건)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>기준년월</TableHead>
                        <TableHead>제약사</TableHead>
                        <TableHead>제품코드</TableHead>
                        <TableHead>제품명</TableHead>
                        <TableHead>보험가</TableHead>
                        <TableHead>수수료율</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.rows.slice(0, 50).map((row, idx) => (
                        <TableRow key={`${row.sourceFile}-${idx}`}>
                          <TableCell>{row.referenceYearMonth}</TableCell>
                          <TableCell>{row.pharmaceuticalCompanyName}</TableCell>
                          <TableCell>{row.drugCode}</TableCell>
                          <TableCell>{row.drugName}</TableCell>
                          <TableCell>
                            {formatNumber(row.price)}
                          </TableCell>
                          <TableCell>{row.standardCommissionRate}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
};
